import { useEffect } from "react";
import { COMPANY, COMPANY_ADDRESS } from "../legal/company";

// SEO po stronie aplikacji: tytuł, opis, link kanoniczny, Open Graph / Twitter, robots i dane strukturalne (JSON-LD).
// Te same wartości generuje prerender na serwerze (backend/seo_build.py) — robot widzi je od razu, a po nawigacji w aplikacji
// hook aktualizuje nagłówek strony.
export const SITE = "https://digilib.store";
export const SITE_NAME = "DigiLib.store";
export const DEFAULT_DESC = "Ebooki, audiobooki, aplikacje i gry edukacyjne z natychmiastową dostawą. Kupujesz raz — korzystasz bez abonamentu. Płatność kartą, BLIKIEM, Apple Pay, Google Pay, Revolut Pay, Klarna lub przelewem.";
export const DEFAULT_IMAGE = `${SITE}/img/og-default.jpg`;

export const ORGANIZATION = {
  "@type": "Organization", "@id": `${SITE}/#organization`, name: SITE_NAME, legalName: COMPANY.name, url: SITE,
  logo: `${SITE}/logo-300.png`, email: COMPANY.email, taxID: COMPANY.nip,
  address: { "@type": "PostalAddress", streetAddress: COMPANY.street, postalCode: COMPANY.postal, addressLocality: COMPANY.city, addressCountry: "PL" },
};

// tekst bez nagłówków pisanych wielkimi literami (np. „CO TO JEST DIGILAW”) — tak samo jak w backend/seo_build.py
const plain = (s) => String(s || "").split("\n")
  .filter((l) => !(l.trim().length < 70 && (l.match(/\p{L}/gu) || []).length > 3 && l.trim() === l.trim().toUpperCase())).join(" ");
export const clip = (s, n = 158) => {
  const t = plain(s).replace(/\s+/g, " ").replace(/[*#_`>]/g, "").trim();
  return t.length <= n ? t : `${t.slice(0, n - 1).replace(/\s+\S*$/, "")}…`;
};
export const abs = (u) => (!u ? DEFAULT_IMAGE : /^https?:/.test(u) ? u : `${SITE}${u.startsWith("/") ? "" : "/"}${u}`);

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (content == null || content === "") { if (el) el.remove(); return; }
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!href) { if (el) el.remove(); return; }
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

export function applySeo({ title, description, path, image, type = "website", noindex = false, jsonLd = null }) {
  const fullTitle = title ? (title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`) : `${SITE_NAME} — ebooki, audiobooki, aplikacje i gry edukacyjne`;
  const desc = clip(description || DEFAULT_DESC);
  const url = path != null ? `${SITE}${path}` : null;
  document.title = fullTitle;
  setMeta("name", "description", desc);
  setMeta("name", "robots", noindex ? "noindex, follow" : "index, follow");
  setLink("canonical", noindex ? null : url);
  setMeta("property", "og:site_name", SITE_NAME);
  setMeta("property", "og:locale", "pl_PL");
  setMeta("property", "og:type", type);
  setMeta("property", "og:title", fullTitle);
  setMeta("property", "og:description", desc);
  setMeta("property", "og:url", url);
  setMeta("property", "og:image", abs(image));
  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", fullTitle);
  setMeta("name", "twitter:description", desc);
  setMeta("name", "twitter:image", abs(image));
  let ld = document.getElementById("seo-jsonld");
  if (jsonLd) {
    if (!ld) { ld = document.createElement("script"); ld.type = "application/ld+json"; ld.id = "seo-jsonld"; document.head.appendChild(ld); }
    ld.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": [].concat(jsonLd) }).replace(/</g, "\\u003c");
  } else if (ld) ld.remove();
}

// Hook dla stron: wywołaj z danymi strony (deps — kiedy przeliczyć, np. po wczytaniu produktu)
export function useSeo(cfg, deps = []) {
  useEffect(() => { if (cfg) applySeo(cfg); }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}

const TYPE_LABEL = { ebook: "Ebook", audiobook: "Audiobook", aplikacja: "Aplikacja", subskrypcja: "Abonament", bon: "Bon podarunkowy" };

export function productSeo(p) {
  const kind = /pakiet/i.test(p.id) ? "Pakiet" : TYPE_LABEL[p.format] || "Produkt";
  const price = Number(p.price || 0).toFixed(2).replace(".", ",");
  const desc = `${clip(p.description, 120)} ${kind} — ${price} zł, dostęp natychmiast po zakupie.`;
  const offer = {
    "@type": "Offer", url: `${SITE}/produkt/${p.id}`, price: Number(p.price || 0).toFixed(2), priceCurrency: "PLN",
    availability: "https://schema.org/InStock", itemCondition: "https://schema.org/NewCondition", seller: { "@id": `${SITE}/#organization` },
  };
  const product = {
    "@type": "Product", "@id": `${SITE}/produkt/${p.id}#product`, name: p.title, description: clip(p.description, 500),
    image: abs(p.cover_image), sku: p.id, category: kind, brand: { "@type": "Brand", name: p.publisher || p.author || SITE_NAME }, offers: offer,
  };
  if (Number(p.reviews) > 0 && Number(p.rating) > 0) {
    product.aggregateRating = { "@type": "AggregateRating", ratingValue: Number(p.rating).toFixed(1), reviewCount: Number(p.reviews), bestRating: 5 };
  }
  const crumbs = {
    "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "Sklep", item: SITE },
      { "@type": "ListItem", position: 2, name: kind, item: p.format === "aplikacja" ? `${SITE}/aplikacje` : SITE },
      { "@type": "ListItem", position: 3, name: p.title, item: `${SITE}/produkt/${p.id}` },
    ],
  };
  return {
    title: `${p.title} — ${kind.toLowerCase()}`, description: desc, path: `/produkt/${p.id}`, image: p.cover_image, type: "product",
    noindex: !!p.hidden, jsonLd: [product, crumbs, ORGANIZATION],
  };
}

export const STATIC_SEO = {
  "/": { title: `${SITE_NAME} — ebooki, audiobooki, aplikacje i gry edukacyjne`, description: DEFAULT_DESC },
  "/aplikacje": { title: "Aplikacje edukacyjne i asystenci AI — kup raz, bez abonamentu", description: "Aplikacje internetowe DigiLib: korepetytor AI, nauka języków, egzamin na prawo jazdy, asystent prawny DigiLaw, gry edukacyjne dla dzieci. Jednorazowa płatność i regularne aktualizacje w cenie." },
  "/blog": { title: "Blog — czytanie, nauka i rozwój", description: "Porady o czytaniu, nauce języków, rozwoju dzieci i korzystaniu z aplikacji edukacyjnych. Artykuły redakcji DigiLib.store." },
  "/bony": { title: "Bon podarunkowy na ebooki, audiobooki i aplikacje", description: "Bony podarunkowe DigiLib.store o wartości 50, 100 i 200 zł — kod wysyłamy e-mailem od razu po zapłacie, ważny 12 miesięcy." },
  "/opinie": { title: "Opinie klientów o DigiLib.store", description: "Opinie klientów DigiLib.store — oznaczenie „zweryfikowany zakup” mają tylko opinie osób z opłaconym zamówieniem." },
  "/o-nas": { title: "O nas — kim jesteśmy i jak działamy", description: "DigiLib.store: cyfrowa biblioteka i aplikacje edukacyjne w modelu „kup raz”. Poznaj naszą misję i zasady działania." },
  "/kontakt": { title: "Kontakt i najczęstsze pytania", description: `Kontakt z DigiLib.store: ${COMPANY.email}. Dane sprzedawcy: ${COMPANY.name}, ${COMPANY_ADDRESS}. Odpowiedzi na najczęstsze pytania.` },
  "/regulamin": { title: "Regulamin sklepu", description: "Regulamin sklepu DigiLib.store: zamówienia, płatności, dostawa treści cyfrowych, licencja, reklamacje i prawo odstąpienia od umowy." },
  "/polityka-prywatnosci": { title: "Polityka prywatności i cookies", description: "Jak DigiLib.store przetwarza dane osobowe (RODO), komu je powierza i jak korzystamy z plików cookies." },
  "/zwroty": { title: "Zwroty, odstąpienie od umowy i reklamacje", description: "Jak złożyć reklamację lub odstąpić od umowy w DigiLib.store, terminy rozpatrzenia i zwrotu pieniędzy." },
  "/wysylka": { title: "Dostawa produktów cyfrowych", description: "Dostawa ebooków, audiobooków i aplikacji DigiLib.store: natychmiast po zapłacie, bez kosztów wysyłki. Formaty i wymagania." },
  "/platnosci": { title: "Sposoby płatności", description: "Płatność kartą Visa i Mastercard, BLIKIEM, Apple Pay, Google Pay, Revolut Pay, Klarna (zapłać później), przelewem tradycyjnym lub saldem konta. Bezpieczne płatności Stripe." },
};

export function useStaticSeo(path, extra = {}) {
  useSeo({ ...STATIC_SEO[path], path, jsonLd: [ORGANIZATION], ...extra }, [path]);
}
